import { LegalLayout } from "@/components/LegalLayout";

const INTRO = [
  "MinistryHub is a software service operated by Up & At ’Em Design Co. LLC (“MinistryHub,” “we,” “us,” or “our”).",
  "This Privacy Policy explains how we collect, use, disclose, and protect personal information when people visit the MinistryHub website, create or administer MinistryHub accounts, interact with us, or submit information through MinistryHub services.",
  "It also explains an important distinction between information MinistryHub collects for its own business operations and information we process on behalf of churches and other organizations using MinistryHub.",
];

const BLOCKS = [
  { t: "h2", text: "1. Our Role" },
  { t: "p", text: "MinistryHub provides software to churches, ministries, and other organizations (“Customers”)." },
  { t: "p", text: "Customers may use MinistryHub to collect information from their guests, members, volunteers, attendees, or other individuals." },
  { t: "p", text: "For information collected by a Customer through MinistryHub, the Customer generally determines:" },
  { t: "ul", items: [
    "What information is collected.",
    "Why it is collected.",
    "How it will be used.",
    "Which integrations receive it.",
    "Which communications or workflows are triggered.",
  ]},
  { t: "p", text: "In those circumstances, MinistryHub generally processes the information on the Customer’s behalf to provide the service." },
  { t: "p", text: "If you have a question about why a particular church or organization collected your information or how that organization uses it, you should contact that organization directly." },

  { t: "h2", text: "2. Information We Collect" },
  { t: "p", text: "Depending on how you interact with MinistryHub, we may collect the following categories of information." },
  { t: "h2", text: "Account and Customer Information" },
  { t: "p", text: "When someone creates or administers a MinistryHub account, we may collect information such as:" },
  { t: "ul", items: [
    "Name.",
    "Email address.",
    "Phone number.",
    "Organization or church name.",
    "Account credentials and authentication information.",
    "Role and permissions.",
    "Billing and subscription information.",
    "Support communications.",
    "Configuration and account preferences.",
  ]},
  { t: "h2", text: "Billing Information" },
  { t: "p", text: "Payments are processed by Stripe or another identified payment provider." },
  { t: "p", text: "We may receive billing-related information such as:" },
  { t: "ul", items: [
    "Customer and subscription identifiers.",
    "Billing status.",
    "Invoice information.",
    "Billing address.",
    "Payment-method type and limited identifying information.",
    "Transaction status.",
  ]},
  { t: "p", text: "MinistryHub does not directly store complete payment-card numbers." },
  { t: "h2", text: "Information Submitted Through Customer Forms" },
  { t: "p", text: "Customers may configure MinistryHub forms or Connect Cards to collect information such as:" },
  { t: "ul", items: [
    "Name.",
    "Email address.",
    "Phone number.",
    "Contact information.",
    "Responses to questions selected by the Customer.",
    "Ministry interests.",
    "Next-step selections.",
    "Other information the Customer chooses to request.",
  ]},
  { t: "p", text: "The exact information collected depends on the Customer’s configuration." },
  { t: "h2", text: "Technical and Usage Information" },
  { t: "p", text: "When MinistryHub is used, we may automatically collect information necessary to operate, secure, and improve the service, including:" },
  { t: "ul", items: [
    "IP address.",
    "Browser and device information.",
    "Login activity.",
    "Dates and times of access.",
    "Pages or features used.",
    "Error and diagnostic information.",
    "Security and authentication events.",
    "Approximate location inferred from network information where applicable.",
  ]},
  { t: "p", text: "We may also collect aggregated usage and engagement information to understand how the platform is performing." },
  { t: "h2", text: "Communications" },
  { t: "p", text: "If you contact us for support, sales, account assistance, or another reason, we may retain the information contained in those communications." },

  { t: "h2", text: "3. How We Use Information" },
  { t: "p", text: "We use personal information as reasonably necessary to:" },
  { t: "ul", items: [
    "Provide MinistryHub services.",
    "Create and administer accounts.",
    "Authenticate users.",
    "Process subscriptions and billing.",
    "Operate Connect Cards and other configured experiences.",
    "Carry out Customer-configured integrations and workflows.",
    "Deliver transactional communications.",
    "Provide customer support.",
    "Detect fraud, abuse, and security threats.",
    "Troubleshoot and maintain the platform.",
    "Understand service performance and usage.",
    "Improve MinistryHub.",
    "Comply with legal obligations.",
    "Enforce our Terms of Service.",
  ]},
  { t: "p", text: "We may use aggregated or de-identified information for analytics, product improvement, reliability, and business planning where that information no longer reasonably identifies an individual." },

  { t: "h2", text: "4. Customer Data" },
  { t: "p", text: "Customers retain ownership of the data they submit to or collect through MinistryHub." },
  { t: "p", text: "We process Customer Data as reasonably necessary to provide MinistryHub and carry out the Customer’s instructions." },
  { t: "p", text: "We do not use a church’s guest or member database to advertise MinistryHub or unrelated products directly to those individuals." },
  { t: "p", text: "We do not claim ownership of a Customer’s guest, member, volunteer, or ministry-participant information." },

  { t: "h2", text: "5. Selling Personal Information" },
  { t: "p", text: "MinistryHub does not sell personal information." },
  { t: "p", text: "We do not sell church databases, Connect Card submissions, contact lists, or other Customer Data to advertisers, data brokers, or other third parties." },

  { t: "h2", text: "6. Service Providers" },
  { t: "p", text: "We use third-party providers to operate portions of MinistryHub. These providers may process information as necessary to provide their services." },
  { t: "p", text: "Depending on MinistryHub’s current configuration and the features a Customer uses, these providers may include services for:" },
  { t: "ul", items: [
    "Cloud hosting and infrastructure.",
    "Database and storage services.",
    "Payment processing.",
    "Email delivery.",
    "Authentication.",
    "Monitoring and security.",
    "Ministry-management integrations.",
  ]},
  { t: "p", text: "For example, MinistryHub may use or integrate with providers such as Stripe, Planning Center, Resend, and Google." },
  { t: "p", text: "Third-party providers process information according to their respective agreements and privacy practices." },

  { t: "h2", text: "7. Planning Center and Other Customer-Connected Integrations" },
  { t: "p", text: "Customers may choose to connect MinistryHub to third-party platforms such as Planning Center." },
  { t: "p", text: "When a Customer enables an integration, MinistryHub may access, transmit, create, or update information in that third-party service according to:" },
  { t: "ul", items: [
    "The permissions granted by the Customer.",
    "The Customer’s MinistryHub configuration.",
    "The functionality of the integration.",
  ]},
  { t: "p", text: "For example, a Customer may configure a MinistryHub Connect Card submission to create or update a person in Planning Center, add information to a workflow, or save a note." },
  { t: "p", text: "MinistryHub does not independently decide to send Customer Data to an optional Customer-connected integration; the Customer enables and configures that connection." },

  { t: "h2", text: "8. Email and Communications" },
  { t: "p", text: "MinistryHub may process information through email-delivery providers to send messages requested or configured by Customers, as well as operational messages from MinistryHub." },
  { t: "p", text: "These may include:" },
  { t: "ul", items: [
    "Form confirmations.",
    "Customer notifications.",
    "Account messages.",
    "Password-reset messages.",
    "Billing or subscription communications.",
    "Security notifications.",
    "Service announcements.",
  ]},
  { t: "p", text: "Customers are responsible for communications they initiate through MinistryHub and for obtaining any consent required for their communications." },

  { t: "h2", text: "9. Cookies and Authentication Technologies" },
  { t: "p", text: "MinistryHub may use cookies and similar technologies that are necessary for:" },
  { t: "ul", items: [
    "Authentication.",
    "Account sessions.",
    "Security.",
    "Preferences.",
    "Service functionality.",
  ]},
  { t: "p", text: "We may also use reasonable analytics technologies to understand how our website or platform is used." },
  { t: "p", text: "Where required by applicable law, we will provide appropriate choices regarding non-essential tracking technologies." },

  { t: "h2", text: "10. Data Retention" },
  { t: "p", text: "We retain information for as long as reasonably necessary to provide MinistryHub, maintain accounts, satisfy contractual obligations, protect the service, resolve disputes, maintain financial records, and comply with applicable law." },
  { t: "p", text: "Customer Data associated with an active account is generally retained while the account remains active." },
  { t: "p", text: "Following termination or expiration of an account, Customers may request available Customer Data for up to 30 days, unless another retention period has been communicated." },
  { t: "p", text: "After that period, Customer Data may be deleted from active systems." },
  { t: "p", text: "Information may remain for additional periods in:" },
  { t: "ul", items: [
    "System backups.",
    "Security logs.",
    "Fraud-prevention records.",
    "Financial and transaction records.",
    "Records we are legally required to retain.",
  ]},
  { t: "p", text: "Backup information is removed according to applicable backup and retention cycles." },

  { t: "h2", text: "11. Data Export and Portability" },
  { t: "p", text: "Customers may access and export supported Customer Data while their account is active." },
  { t: "p", text: "MinistryHub is committed to reasonable data portability and does not intend to use technical restrictions to unnecessarily prevent Customers from retrieving their church-owned information." },
  { t: "p", text: "Available export tools may vary by feature and may be expanded as MinistryHub develops." },
  { t: "p", text: "Customers may also contact us regarding an available export during the post-termination retention period described above." },

  { t: "h2", text: "12. Security" },
  { t: "p", text: "We use reasonable administrative, technical, and organizational safeguards designed to protect information against unauthorized access, loss, misuse, alteration, or disclosure." },
  { t: "p", text: "These safeguards may include authentication controls, encrypted connections, access restrictions, credential protections, monitoring, and other measures appropriate to the service." },
  { t: "p", text: "No internet-based service can guarantee absolute security. Customers are also responsible for securing their own accounts, devices, administrator access, and connected third-party services." },

  { t: "h2", text: "13. Children’s Privacy" },
  { t: "p", text: "MinistryHub is a business service provided to churches and organizations and is not directed primarily to children." },
  { t: "p", text: "Customers may use MinistryHub in connection with ministries involving minors. In those circumstances, the Customer determines what information it collects and is responsible for complying with applicable parental-consent, child-privacy, and other legal requirements." },
  { t: "p", text: "If you believe information about a child has been collected improperly through a MinistryHub Customer, please contact the applicable church or organization. You may also contact MinistryHub if the issue concerns our processing of that information." },

  { t: "h2", text: "14. Your Privacy Choices and Rights" },
  { t: "p", text: "Depending on where you live, applicable law may provide rights concerning your personal information, which may include rights to:" },
  { t: "ul", items: [
    "Request access to certain personal information.",
    "Request correction of inaccurate information.",
    "Request deletion of certain information.",
    "Obtain a copy of certain information.",
    "Object to or restrict certain processing.",
    "Withdraw consent where processing is based on consent.",
  ]},
  { t: "p", text: "These rights are subject to applicable legal limitations and exceptions." },
  { t: "p", text: "If your information was collected by a church or organization through MinistryHub, you should generally direct your request to that organization because it determines how the information is used." },
  { t: "p", text: "We will reasonably assist Customers with appropriate privacy requests relating to information we process on their behalf." },
  { t: "p", text: "For information MinistryHub controls directly, you may contact us using the information below." },

  { t: "h2", text: "15. Disclosure Required by Law or for Safety" },
  { t: "p", text: "We may disclose information when we reasonably believe disclosure is necessary to:" },
  { t: "ul", items: [
    "Comply with applicable law, legal process, or lawful governmental requests.",
    "Protect the rights, property, or safety of MinistryHub, our Customers, users, or others.",
    "Investigate fraud, abuse, or security incidents.",
    "Enforce our agreements.",
    "Protect the integrity of the service.",
  ]},
  { t: "p", text: "Where legally permitted and appropriate, we will seek to limit disclosure to information reasonably necessary for the applicable purpose." },

  { t: "h2", text: "16. Business Transfers" },
  { t: "p", text: "If MinistryHub or Up & At ’Em Design Co. LLC is involved in a merger, acquisition, financing, reorganization, sale of assets, or similar business transaction, information may be transferred as part of that transaction." },
  { t: "p", text: "Any successor receiving personal information will remain subject to applicable privacy obligations regarding that information." },

  { t: "h2", text: "17. International Processing" },
  { t: "p", text: "MinistryHub and its service providers may process information in the United States and other locations where applicable providers operate." },
  { t: "p", text: "If MinistryHub expands materially into jurisdictions requiring additional international data-transfer mechanisms or privacy terms, we may implement additional safeguards and update this Privacy Policy accordingly." },

  { t: "h2", text: "18. Changes to This Privacy Policy" },
  { t: "p", text: "We may update this Privacy Policy as MinistryHub changes or as legal, regulatory, operational, or security requirements evolve." },
  { t: "p", text: "When changes are material, we will provide reasonable notice through the service, by email, on our website, or through another appropriate method." },
  { t: "p", text: "The effective date at the top of this Policy indicates when the current version became effective." },

  { t: "h2", text: "19. Contact Us" },
  { t: "p", text: "Questions or requests concerning this Privacy Policy may be directed to:" },
  { t: "p", text: "MinistryHub" },
  { t: "p", text: "Operated by Up & At ’Em Design Co. LLC" },
  { t: "p", text: "Email: hello@getministryhub.com" },
  { t: "p", text: "Website: getministryhub.com" },
];

export default function Privacy() {
  return (
    <LegalLayout
      testid="privacy-page"
      docTitle="MinistryHub Privacy Policy"
      title="MinistryHub Privacy Policy"
      effectiveDate="August 24, 2026"
      intro={INTRO}
      blocks={BLOCKS}
    />
  );
}
